import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { ShoppingBag } from "@mui/icons-material";
import { useRenderPerformance } from "@/components/utils/performance";
import { numprice } from "@/helpers";

interface FloatingCartButtonProps {
  itemCount: number;
  totalSum: number;
  onClick: () => void;
  isEmpty: boolean;
}

/**
 * Floating Cart Button Component
 * Positioned bottom right with cart count and total price
 */
export const FloatingCartButton: React.FC<FloatingCartButtonProps> = React.memo(
  ({ itemCount, totalSum, onClick, isEmpty }) => {
    useRenderPerformance("FloatingCartButton");

    const theme = useTheme();

    const getPositionText = (count: number) => {
      if (count === 1) return "позиция";
      if (count >= 2 && count <= 4) return "позиции";
      return "позиций";
    };

    if (isEmpty) return null;

    return (
      <Box
        sx={{
          position: "fixed",
          bottom: theme.spacing(2.5), // 20px
          right: theme.spacing(2.5), // 20px
          zIndex: 1000,
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.9)"
              : "rgba(23, 24, 24, 0.9)",
          backdropFilter: "blur(8px)",
          borderRadius: theme.spacing(2), // 16px
          padding: theme.spacing(2), // 16px
          cursor: "pointer",
          transition: theme.transitions.create(
            ["background-color", "transform"],
            {
              duration: theme.transitions.duration.short,
            }
          ),
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.95)"
                : "rgba(23, 24, 24, 0.95)",
            transform: "translateY(-2px)",
          },
        }}
        onClick={onClick}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1.25), // 10px
          }}
        >
          <ShoppingBag
            sx={{
              color:
                theme.palette.mode === "dark"
                  ? "rgba(0, 0, 0, 0.6)"
                  : "rgba(255, 255, 255, 0.6)",
              fontSize: theme.spacing(2.5), // 20px
            }}
          />
          <Typography
            sx={{
              fontSize: "15px",
              lineHeight: "20px",
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.common.black
                  : theme.palette.common.white,
              fontWeight: 400,
              whiteSpace: "nowrap",
            }}
          >
            {itemCount} {getPositionText(itemCount)}{" "}
            <Box
              component="span"
              sx={{
                color:
                  theme.palette.mode === "dark"
                    ? "rgba(0, 0, 0, 0.41)"
                    : "rgba(255, 255, 255, 0.41)",
                fontWeight: 400,
              }}
            >
              ×
            </Box>{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 500,
              }}
            >
              {numprice(totalSum)} ₽
            </Box>
          </Typography>
        </Box>
      </Box>
    );
  }
);

FloatingCartButton.displayName = "FloatingCartButton";
